import {
    FC,
    MouseEvent,
    ReactElement,
    useEffect,
    useState,
} from 'react';

import classNames from 'classnames';
import { useToggle } from 'react-use';

import { Button, Card } from '../../../../../components';
import { IconButton, SubmitButton } from '../../../../../compositions';
import trans from '../../../../../helpers/trans';
import { DevelopmentPlanSkill, EditDevelopmentPlanSkillFormData } from '../../../../../models/DevelopmentPlanSkill/DevelopmentPlanSkill';
import { DevelopmentPlanCardQuestion } from '..';

import './DevelopmentPlanSkillCard.scss';

interface DevelopmentPlanSkillCardProps {
    isLoading: boolean;
    isSuccessful: boolean;
    skill: DevelopmentPlanSkill;
    onSubmit: (formData: EditDevelopmentPlanSkillFormData) => void;
    onDelete: (skill: DevelopmentPlanSkill) => void;
    className?: string;
}

const DevelopmentPlanSkillCard: FC<DevelopmentPlanSkillCardProps> = ({
    isLoading,
    isSuccessful,
    skill,
    onSubmit,
    onDelete,
    className = '',
}): ReactElement => {
    const [hasChanges, setHasChanges] = useState(false);
    const [remarks, setRemarks] = useState<string>(skill.remarks);
    const [isCompleted, toggleIsCompleted] = useToggle(skill.isCompleted);
    const [isExpanded, toggleIsExpanded] = useToggle(false);
    const [isEditing, toggleIsEditing] = useToggle(false);

    const expandableSectionClasses = classNames('development-plan-skill-card__expandable-section', {
        'development-plan-skill-card__expandable-section--is-expanded': isExpanded,
    });
    const toggleIconClasses = classNames('development-plan-skill-card__toggle-icon', {
        'development-plan-skill-card__toggle-icon--is-expanded': isExpanded,
    });

    const handleSubmit = (): void => {
        const formData = {
            id: skill.id,
            remarks,
            isCompleted,
        };

        onSubmit(formData);
    };

    const handleSubmitButton = (event: MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();
        handleSubmit();
    };

    const handleEdit = (): void => {
        toggleIsExpanded(true);
        toggleIsEditing(true);
    };

    const handleChange = (setFunction: React.Dispatch<React.SetStateAction<string>>) => (value: string): void => {
        setFunction(value);
        setHasChanges(true);
    };

    const handleSubmitIsCompleted = (): void => {
        const formData = {
            id: skill.id,
            remarks,
            isCompleted: !isCompleted,
        };

        onSubmit(formData);
    };

    const handleComplete = (): void => {
        toggleIsCompleted();
        handleSubmitIsCompleted();
    };

    const handleDelete = (): void => {
        onDelete(skill);
    };

    useEffect((): void => {
        if (isSuccessful) {
            setHasChanges(false);
            toggleIsEditing(false);
        }
    }, [isSuccessful]);

    return (
        <Card className={`development-plan-skill-card ${className}`}>
            <form>
                <div className="development-plan-skill-card__header">
                    <div className="development-plan-skill-card__title-wrapper">
                        <IconButton
                            text={`${trans('common.skill')}: ${skill.name}`}
                            icon="chevron-down"
                            onClick={toggleIsExpanded}
                            disabled={hasChanges}
                            className="development-plan-skill-card__toggle-button"
                            iconClassName={toggleIconClasses}
                        />
                    </div>
                    <div className="development-plan-skill-card__action-buttons">
                        {!isEditing && (
                            <>
                                {!skill.isCompleted && (
                                    <Button
                                        disabled={isLoading}
                                        text={trans('containers.developmentPlanOverview.developmentPlanSkillCard.complete')}
                                        onClick={handleComplete}
                                        className="development-plan-skill-card__complete-button"
                                    />
                                )}

                                {skill.isCompleted && (
                                    <IconButton
                                        disabled={isLoading}
                                        text={trans('containers.developmentPlanOverview.developmentPlanSkillCard.isCompleted')}
                                        icon="check"
                                        onClick={handleComplete}
                                        className="development-plan-skill-card__complete-button"
                                    />
                                )}

                                <IconButton
                                    icon="edit"
                                    text={trans('common.edit')}
                                    hideLabel
                                    onClick={handleEdit}
                                    className="development-plan-skill-card__action-button"
                                />
                            </>
                        )}

                        {isEditing && (
                            <SubmitButton
                                isLoading={isLoading}
                                text={trans('common.saveChanges')}
                                icon="disk"
                                onClick={handleSubmitButton}
                                className="development-plan-skill-card__save-button"
                            />
                        )}

                        <IconButton
                            icon="bin"
                            text={trans('common.delete')}
                            hideLabel
                            onClick={handleDelete}
                            className="development-plan-skill-card__action-button development-plan-skill-card__action-button--is-delete"
                        />
                    </div>
                </div>
                <div className={expandableSectionClasses}>
                    <DevelopmentPlanCardQuestion
                        isEditing={isEditing}
                        label={trans('containers.developmentPlanOverview.developmentPlanSkillCard.description')}
                        placeholder={trans('containers.developmentPlanOverview.developmentPlanSkillCard.descriptionPlaceholder')}
                        value={remarks}
                        onChange={handleChange(setRemarks)}
                        className="development-plan-skill-card__question"
                    />
                </div>
            </form>
        </Card>
    );
};

export default DevelopmentPlanSkillCard;
